// energySystem.ts: Comprehensive energy flow and visualization system
// Tracks metabolic, psychionic, neural, organ, and vibrational energy throughout the network

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { LifeBehaviorMathEngine, MetabolicParameters } from './lifeBehaviorMath';
import { Node } from './types';

export interface EnergyFlow {
    from: number;        // Source node ID
    to: number;          // Target node ID
    energyType: EnergyType;
    rate: number;        // Energy flow rate (0-1)
    intensity: number;   // Visual intensity (0-1)
    progress: number;    // Animation progress (0-1)
    timestamp: number;
}

export type EnergyType = 
    | 'neural'           // Neural electrical activity
    | 'metabolic'        // Cellular metabolism (ATP energy)
    | 'psychionic'       // Psychic/vibrational energy
    | 'organ'            // Energy from organs
    | 'breathing'        // Respiratory energy
    | 'vibrational'      // Wave/vibrational energy
    | 'creative'         // Creative/cognitive energy
    | 'emotional'         // Emotional energy
    | 'transcendent';     // Transcendent energy

export interface NodeEnergyState {
    nodeId: number;
    neuralEnergy: number;      // 0-1
    metabolicEnergy: number;   // 0-1
    psychionicEnergy: number;   // 0-1
    organEnergy: number;       // 0-1
    breathingEnergy: number;   // 0-1
    vibrationalEnergy: number; // 0-1
    creativeEnergy: number;    // 0-1
    emotionalEnergy: number;   // 0-1
    transcendentEnergy: number; // 0-1
    totalEnergy: number;       // 0-1, weighted sum
    energyCapacity: number;    // Max energy
    lastUpdate: number;
}

export interface OrganEnergyField {
    organId: number;
    energyType: EnergyType;
    center: { x: number; y: number; z: number };
    radius: number;
    intensity: number;
    phase: number;
    frequency: number;
}

export class EnergySystem {
    private network: NeuralNetworkSimulator;
    private mathEngine: LifeBehaviorMathEngine;
    
    // Energy states for all nodes
    private nodeEnergyStates: Map<number, NodeEnergyState> = new Map();
    
    // Active energy flows
    private energyFlows: EnergyFlow[] = [];
    private maxFlows: number = 100;
    
    // Organ energy fields
    private organFields: OrganEnergyField[] = [];
    
    // Psychionic cascades (dramatic energy bursts)
    private psychionicCascades: Array<{
        origin: number;
        nodes: number[];
        intensity: number;
        radius: number;
        time: number;
    }> = [];
    
    // Energy pulse tracking
    private lastEnergyPulse: number = 0;
    private pulseCooldown: number = 100; // ms
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.mathEngine = new LifeBehaviorMathEngine();
    }
    
    public update(dt: number): void {
        this.updateNodeEnergyStates(dt);
        this.updateEnergyFlows(dt);
        this.updateOrganFields(dt);
        this.updatePsychionicCascades(dt);
    }
    
    private updateNodeEnergyStates(dt: number): void {
        for (const node of this.network.nodes) {
            if (!node || node.corroded) continue;
            
            let state = this.nodeEnergyStates.get(node.id);
            if (!state) {
                state = this.createInitialEnergyState(node);
                this.nodeEnergyStates.set(node.id, state);
            }
            
            // Update neural energy based on node activity
            state.neuralEnergy = Math.min(1, node.energy || 0);
            
            // Determine elemental influence for this node
            const elem = this.getElementForNode(node as Node);
            if (elem && (node as Node).element !== elem) {
                (node as Node).element = elem;
            }

            const elemental = this.getElementalModifiers(elem);

            // Update metabolic energy using life behavior math with elemental modifiers
            const metabolicParams: MetabolicParameters = {
                currentEnergy: state.metabolicEnergy,
                productionRate: this.calculateMetabolicRate(node) * elemental.productionMultiplier,
                consumptionRate: 0.1 * elemental.consumptionMultiplier,
                efficiencyFactor: 0.8 * elemental.efficiencyMultiplier,
                maxEnergy: 1.0
            };
            state.metabolicEnergy = this.mathEngine.metabolicModel.calculateEnergyDynamics(metabolicParams, dt);
            
            // Update psychionic energy (based on network complexity)
            state.psychionicEnergy = Math.min(1, this.network.linguisticComplexity / 10);
            
            // Update organ energy (if organs exist)
            if (this.network.organSystem) {
                state.organEnergy = this.calculateOrganEnergy(node);
            }
            
            // Update breathing energy
            if (this.network.breathingSystem) {
                state.breathingEnergy = this.calculateBreathingEnergy();
            }
            
            // Update vibrational energy (wave patterns)
            state.vibrationalEnergy = this.calculateVibrationalEnergy(node);
            
            // Update creative energy
            state.creativeEnergy = this.calculateCreativeEnergy(node);
            
            // Update emotional energy
            state.emotionalEnergy = this.calculateEmotionalEnergy(node);
            
            // Update transcendent energy
            state.transcendentEnergy = this.calculateTranscendentEnergy();
            
            // Calculate total energy (weighted sum)
            state.totalEnergy = (
                state.neuralEnergy * 0.25 +
                state.metabolicEnergy * 0.20 +
                state.psychionicEnergy * 0.15 +
                state.organEnergy * 0.15 +
                state.breathingEnergy * 0.10 +
                state.vibrationalEnergy * 0.05 +
                state.creativeEnergy * 0.05 +
                state.emotionalEnergy * 0.03 +
                state.transcendentEnergy * 0.02
            );
            
            state.lastUpdate = Date.now();
        }
    }
    
    private createInitialEnergyState(node: Node): NodeEnergyState {
        return {
            nodeId: node.id,
            neuralEnergy: node.energy || 0,
            metabolicEnergy: 0.5,
            psychionicEnergy: 0.3,
            organEnergy: 0,
            breathingEnergy: 0,
            vibrationalEnergy: 0,
            creativeEnergy: 0.2,
            emotionalEnergy: 0.1,
            transcendentEnergy: 0.05,
            totalEnergy: 0.5,
            energyCapacity: 1.0,
            lastUpdate: Date.now()
        };
    }
    
    private calculateMetabolicRate(node: Node): number {
        // Metabolic rate based on node connections
        const connections = this.network.edges.filter(e => e.from === node.id || e.to === node.id).length;
        return 0.5 + Math.min(0.5, connections / 10);
    }

    // --- Elemental model integration ---
    private getElementForNode(node: Node): 'Fire' | 'Earth' | 'Air' | 'Water' | null {
        const hueMatch = node.color?.match(/hsl\((\d+)/);
        const hue = hueMatch ? parseInt(hueMatch[1]) : NaN;
        if (isNaN(hue)) return null;
        // Map hue to zodiac (keeps parity with UI tooltip) then to element
        let zodiac = '';
        if (hue >= 60 && hue < 80) zodiac = 'Taurus';
        else if (hue >= 80 && hue < 100) zodiac = 'Capricorn';
        else if (hue >= 100 && hue < 120) zodiac = 'Scorpio';
        else if (hue >= 120 && hue < 150) zodiac = 'Cancer';
        else if (hue >= 60 && hue < 150) zodiac = 'Virgo';
        else if (hue >= 150 && hue < 200) zodiac = 'Gemini';
        else if (hue >= 200 && hue < 260) zodiac = 'Aquarius';
        else if (hue >= 260 && hue < 320) zodiac = 'Pisces';
        else if (hue >= 320 && hue < 340) zodiac = 'Libra';
        else if (hue >= 40 && hue < 60) zodiac = 'Sagittarius';
        else if (hue >= 20 && hue < 40) zodiac = 'Leo';
        else if (hue < 20 || hue >= 340) zodiac = 'Aries';

        // Zodiac to element
        const fire = ['Aries', 'Leo', 'Sagittarius'];
        const earth = ['Taurus', 'Virgo', 'Capricorn'];
        const air = ['Gemini', 'Libra', 'Aquarius'];
        const water = ['Cancer', 'Scorpio', 'Pisces'];
        if (fire.includes(zodiac)) return 'Fire';
        if (earth.includes(zodiac)) return 'Earth';
        if (air.includes(zodiac)) return 'Air';
        if (water.includes(zodiac)) return 'Water';
        return null;
    }

    private getElementalModifiers(element: 'Fire' | 'Earth' | 'Air' | 'Water' | null): { productionMultiplier: number; consumptionMultiplier: number; efficiencyMultiplier: number } {
        switch (element) {
            case 'Fire':
                // Fire: high production, higher consumption
                return { productionMultiplier: 1.15, consumptionMultiplier: 1.08, efficiencyMultiplier: 1.0 };
            case 'Earth':
                // Earth: steady, efficient, lower consumption
                return { productionMultiplier: 1.0, consumptionMultiplier: 0.92, efficiencyMultiplier: 1.08 };
            case 'Air':
                // Air: efficient transfer/processing
                return { productionMultiplier: 1.05, consumptionMultiplier: 0.98, efficiencyMultiplier: 1.12 };
            case 'Water':
                // Water: adaptive, reduces consumption more
                return { productionMultiplier: 0.98, consumptionMultiplier: 0.88, efficiencyMultiplier: 1.05 };
            default:
                return { productionMultiplier: 1.0, consumptionMultiplier: 1.0, efficiencyMultiplier: 1.0 };
        }
    }
    
    private calculateOrganEnergy(node: Node): number {
        if (!this.network.organSystem) return 0;
        const organs = this.network.organSystem.getOrgans();
        
        // Sum energy from nearby organs
        let totalOrganEnergy = 0;
        for (const organ of organs) {
            const dx = node.x - organ.location.x;
            const dy = node.y - organ.location.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 200) {
                const influence = (organ.energy / organ.energyCapacity) * Math.exp(-distance / 100);
                totalOrganEnergy += influence;
            }
        }
        
        return Math.min(1, totalOrganEnergy / 3); // Max 3 organs contributing
    }
    
    private calculateBreathingEnergy(): number {
        if (!this.network.breathingSystem) return 0;
        // Get breathing state energy
        const breathing = (this.network.breathingSystem as any).state;
        if (!breathing) return 0;
        
        return Math.min(1, (breathing.oxygenLevel || 0) * 0.5 + (breathing.breathDepth || 0) * 0.5);
    }
    
    private calculateVibrationalEnergy(node: Node): number {
        // Based on wave patterns in network
        const waveIntensity = this.network.wavePatterns?.reduce((sum, w) => sum + Math.abs(w), 0) / 
                              Math.max(1, this.network.wavePatterns?.length || 1);
        return Math.min(1, waveIntensity);
    }
    
    private calculateCreativeEnergy(node: Node): number {
        // Based on conceptual abstraction system
        if (!this.network.conceptualAbstraction) return 0;
        const concepts = this.network.conceptualAbstraction.getAbstractConcepts();
        return Math.min(1, concepts.length / 20);
    }
    
    private calculateEmotionalEnergy(node: Node): number {
        // Based on language system emotional concepts
        if (!this.network.livingLanguage) return 0;
        const emotions = ['love', 'joy', 'fear', 'sadness', 'anger', 'excitement', 'serenity'];
        let emotionalSum = 0;
        for (const emotion of emotions) {
            // Check if emotion concept exists
            emotionalSum += 0.1;
        }
        return Math.min(1, emotionalSum);
    }
    
    private calculateTranscendentEnergy(): number {
        // Transcendent energy is rare and powerful
        return Math.min(1, (this.network.linguisticComplexity / 20) * (this.network.totalNetworkEnergy / 1000));
    }
    
    private updateEnergyFlows(dt: number): void {
        const now = Date.now();
        const time = this.network.step / 60;
        
        // Filter old flows
        this.energyFlows = this.energyFlows.filter(flow => now - flow.timestamp < 2000);
        
        // Create new flows along active edges
        for (const edge of this.network.edges) {
            const fromNode = this.network.nodes[edge.from];
            const toNode = this.network.nodes[edge.to];
            
            if (!fromNode || !toNode || fromNode.corroded || toNode.corroded) continue;
            
            const fromEnergy = this.nodeEnergyStates.get(fromNode.id);
            const toEnergy = this.nodeEnergyStates.get(toNode.id);
            
            if (!fromEnergy || !toEnergy) continue;
            
            // Only create flows when there's significant energy difference
            const energyDelta = fromEnergy.totalEnergy - toEnergy.totalEnergy;
            
            if (Math.abs(energyDelta) > 0.1 && this.energyFlows.length < this.maxFlows) {
                // Determine energy type based on network state
                const energyType = this.determineEnergyType(fromEnergy, edge.weight);
                
                // Check if flow already exists
                const existingFlow = this.energyFlows.find(
                    f => f.from === fromNode.id && f.to === toNode.id && f.energyType === energyType
                );
                
                if (!existingFlow) {
                    this.energyFlows.push({
                        from: fromNode.id,
                        to: toNode.id,
                        energyType,
                        rate: Math.abs(energyDelta) * edge.weight,
                        intensity: Math.abs(energyDelta) * 0.8,
                        progress: Math.random(), // Random start position for visual effect
                        timestamp: now
                    });
                } else {
                    // Update existing flow
                    existingFlow.rate = Math.abs(energyDelta) * edge.weight;
                    existingFlow.intensity = Math.abs(energyDelta) * 0.8;
                }
            }
        }
        
        // Update flow animations
        for (const flow of this.energyFlows) {
            flow.progress += flow.rate * dt * 3; // Speed up animation
            if (flow.progress >= 1) {
                flow.progress = 0; // Loop animation
            }
        }
    }
    
    private determineEnergyType(energy: NodeEnergyState, edgeWeight: number): EnergyType {
        // Determine which type of energy is dominant
        const energies = [
            { type: 'neural' as EnergyType, value: energy.neuralEnergy },
            { type: 'metabolic' as EnergyType, value: energy.metabolicEnergy },
            { type: 'psychionic' as EnergyType, value: energy.psychionicEnergy },
            { type: 'organ' as EnergyType, value: energy.organEnergy },
            { type: 'breathing' as EnergyType, value: energy.breathingEnergy },
            { type: 'vibrational' as EnergyType, value: energy.vibrationalEnergy },
            { type: 'creative' as EnergyType, value: energy.creativeEnergy }
        ];
        
        energies.sort((a, b) => b.value - a.value);
        
        // Return dominant energy type
        return energies[0].type;
    }
    
    private updateOrganFields(dt: number): void {
        if (!this.network.organSystem) return;
        
        const organs = this.network.organSystem.getOrgans();
        this.organFields = [];
        
        const time = this.network.step / 60;
        
        for (const organ of organs) {
            if (organ.state === 'active') {
                this.organFields.push({
                    organId: organ.id,
                    energyType: 'organ',
                    center: organ.location,
                    radius: 50 + organ.size * 20,
                    intensity: organ.energy / organ.energyCapacity,
                    phase: time * 2,
                    frequency: 0.5
                });
            }
        }
    }
    
    private updatePsychionicCascades(dt: number): void {
        // Remove old cascades
        this.psychionicCascades = this.psychionicCascades.filter(
            c => Date.now() - c.time < 2000
        );
        
        // Rarely spawn psychionic cascades
        if (Math.random() < 0.001 && this.network.linguisticComplexity > 5) {
            const node = this.network.nodes[Math.floor(Math.random() * this.network.nodes.length)];
            if (!node || node.corroded) return;
            
            // Find nearby nodes for the cascade
            const nearbyNodes = this.network.nodes.filter(n => {
                const dx = n.x - node.x;
                const dy = n.y - node.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                return dist < 100;
            }).map(n => n.id);
            
            this.psychionicCascades.push({
                origin: node.id,
                nodes: nearbyNodes,
                intensity: 1.0,
                radius: 100,
                time: Date.now()
            });
        }
    }
    
    // Public interface
    public getNodeEnergyState(nodeId: number): NodeEnergyState | undefined {
        return this.nodeEnergyStates.get(nodeId);
    }
    
    public getEnergyFlows(): EnergyFlow[] {
        return [...this.energyFlows];
    }
    
    public getOrganFields(): OrganEnergyField[] {
        return [...this.organFields];
    }
    
    public getPsychionicCascades(): typeof this.psychionicCascades {
        return [...this.psychionicCascades];
    }
    
    public getTotalEnergy(): number {
        let total = 0;
        for (const state of this.nodeEnergyStates.values()) {
            total += state.totalEnergy;
        }
        return total / Math.max(1, this.nodeEnergyStates.size);
    }
}

